// TournamentDetailPage — Phase T2.0A Prompt 2 member-facing event page.
//
// Layout when the event is in `registration_open` / `registration_closed`:
//   1. Flyer / poster (if uploaded)
//   2. Event header (title + composition/type + status)
//   3. Event Info card (date, time, location, format, fee, capacity)
//   4. Registration CTA / state card (open + registered / withdraw / full)
//   5. Registered Players roster (Prompt 1.1 layout)
//   6. Event description (if set)
//   7. Event Details (match format text, if set)
//
// Later lifecycle stages (`bracket_generated`, `in_progress`, `completed`)
// keep the compact entry-list + BracketView + Champion strip so the
// competition experience is preserved.
//
// The Withdraw action opens a mobile-safe confirmation dialog rather than
// window.confirm(), removing the last "accidental single-tap" vector.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CaretLeft, Gear, Trophy } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { tournamentAPI } from '../lib/tournamentApi';
import { registrationAPI } from '../lib/registrationApi';

import BracketView from '../components/tournaments/BracketView';
import MemberLink from '../components/MemberLink';
import RegistrationRoster from '../components/registration/RegistrationRoster';
import EventRegistrationDialog from '../components/registration/EventRegistrationDialog';

import EventPhotoGallery from '../components/events/EventPhotoGallery';
import EventHeader from '../components/events/EventHeader';
import EventInfoCard from '../components/events/EventInfoCard';
import RegistrationCTA from '../components/events/RegistrationCTA';
import PaymentSection from '../components/events/PaymentSection';
import PostRegistrationPaymentPrompt from '../components/events/PostRegistrationPaymentPrompt';
import AlternateList from '../components/events/AlternateList';
import { eventIsFull } from '../lib/capacity';

// Doubles: "A / B" with each half separately linked.
function EntryName({ entry, className = '' }) {
  if (!entry) return null;
  const { player1_profile_id, player2_profile_id, player1_name, player2_name, display_name } = entry;
  if (!player2_profile_id && !player2_name) {
    return <MemberLink profileId={player1_profile_id} name={player1_name || display_name} className={className} />;
  }
  return (
    <span className={className}>
      <MemberLink profileId={player1_profile_id} name={player1_name} />
      <span className="text-[#9aa39d]"> / </span>
      <MemberLink profileId={player2_profile_id} name={player2_name} />
    </span>
  );
}

// Format entry fee cents → "$25" / "$25.50". Returns "" when unset/zero.
function formatFeeLabel(cents) {
  if (cents == null || cents <= 0) return '';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

export default function TournamentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEventManager = Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'tournament' && m.event_id === id,
  );
  const canManage = isAdmin || isEventManager;

  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  const [regOpen, setRegOpen] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  // Post-registration payment prompt orchestration.
  const [payPromptOpen, setPayPromptOpen] = useState(false);
  const [autoStartToken, setAutoStartToken] = useState(0);
  const justRegisteredRef = useRef(false);

  const reload = () => tournamentAPI.get(id).then((r) => setT(r.data)).catch(() => {});

  useEffect(() => {
    tournamentAPI.get(id)
      .then((r) => setT(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load.'));
  }, [id]);

  // Derived state — computed before early returns so hooks below stay
  // called unconditionally on every render.
  const myEntryEarly = (t?.entries || []).find((e) => {
    if (!user?.id) return false;
    if (e.player1_profile_id === user.id) return !!e.player1_registered_at;
    if (e.player2_profile_id === user.id) return !!e.player2_registered_at;
    return false;
  });
  const isRegisteredEarly = !!myEntryEarly;
  const paidProfileIdsEarly = new Set(
    (t?.payments || []).filter((p) => p.paid).map((p) => p.profile_id),
  );
  const viewerPaidEarly = !!user?.id && paidProfileIdsEarly.has(user.id);
  const paymentEnabledEarly = !!t?.payment_type && (t?.entry_fee_cents || 0) > 0;

  // Fire the post-registration payment prompt exactly once per successful
  // registration action. `justRegisteredRef` guards against re-showing on
  // page revisits or subsequent reloads.
  useEffect(() => {
    if (!t) return;
    if (!justRegisteredRef.current) return;
    if (!isRegisteredEarly) return;                // wait until reload confirms
    justRegisteredRef.current = false;
    if (!paymentEnabledEarly || viewerPaidEarly) return;
    setPayPromptOpen(true);
  }, [t, isRegisteredEarly, paymentEnabledEarly, viewerPaidEarly]);

  if (err) return <div className="text-sm text-red-700" data-testid="tournament-detail-error">{err}</div>;
  if (!t) return <div className="text-sm text-[#6a766e]" data-testid="tournament-detail-loading">Loading…</div>;

  // A caller is "personally registered" when they occupy one of the two
  // slots AND that slot's registered_at timestamp is set. Naming a
  // partner never falsely counts as their own registration (Feb 2026
  // corrected doubles semantics).
  const myEntry = myEntryEarly;
  const isRegistered = isRegisteredEarly;

  // Am I currently sitting on someone else's entry as a named partner
  // without having personally registered yet? Drives the "You were named
  // as X's partner — tap Register to confirm" affordance in the CTA.
  const myNamedSlotEntry = (t.entries || []).find((e) => {
    if (!user?.id || myEntry) return false;
    if (e.player1_profile_id === user.id && !e.player1_registered_at) return true;
    if (e.player2_profile_id === user.id && !e.player2_registered_at) return true;
    return false;
  });
  const isFull = eventIsFull(t.entries || [], t.tournament_type, t.max_entries);
  const preCompetition = t.status === 'registration_open' || t.status === 'registration_closed';

  // Alternate list (Prompt 2.2). The backend returns a FIFO ordered array
  // already; we just check if the viewer is on it.
  const alternates = t.alternates || [];
  const myAlternate = user?.id
    ? alternates.find((a) => a.profile_id === user.id) : null;
  const isAlternate = !!myAlternate;

  const paidProfileIds = paidProfileIdsEarly;
  const viewerPaid = viewerPaidEarly;
  const paymentEnabled = paymentEnabledEarly;
  const amountLabel = formatFeeLabel(t.entry_fee_cents);

  const handlePayNow = () => {
    setPayPromptOpen(false);
    // Venmo/Zelle open the configured link in a new tab. Stripe scrolls
    // to & auto-opens the embedded iframe on the same page. Cash never
    // renders a Pay Now button so it never lands here.
    if (t.payment_type === 'venmo' || t.payment_type === 'zelle') {
      if (t.payment_link) window.open(t.payment_link, '_blank', 'noopener,noreferrer');
      return;
    }
    requestAnimationFrame(() => {
      const el = document.getElementById('event-payment-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAutoStartToken((n) => n + 1);
    });
  };

  const handlePayLater = () => {
    setPayPromptOpen(false);
  };

  async function handleWithdraw() {
    setWithdrawBusy(true);
    try {
      await registrationAPI.tournamentUnregister(id);
      toast.success('You have been withdrawn.');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Could not withdraw.');
    } finally {
      setWithdrawBusy(false);
    }
  }

  const headerActions = canManage && (
    <Link
      to={`/tournaments/${t.id}/manage`}
      data-testid="manage-tournament-btn"
      className="px-3 py-2 rounded-full bg-[#013523] text-white text-sm flex items-center gap-1.5 hover:bg-[#024029] transition-colors min-h-[44px]"
    >
      <Gear size={14} /> Manage
    </Link>
  );

  return (
    <div className="space-y-5" data-testid="tournament-detail">
      <button
        data-testid="tournament-back"
        onClick={() => navigate('/tournaments')}
        className="text-xs text-[#6a766e] hover:text-[#013523] flex items-center gap-1"
      >
        <CaretLeft size={14} /> Tournaments
      </button>

      {/* 1. Flyer / poster + additional photos (optional). */}
      <EventPhotoGallery flyerUrl={t.flyer_url} eventName={t.name} kind="tournament" eventId={t.id} />

      {/* 2. Event header. */}
      <EventHeader
        name={t.name}
        composition={t.composition || 'open'}
        eventType={t.tournament_type}
        status={t.status}
        actions={headerActions}
      />

      {/* 3. Event Info card — only during pre-competition or when set. */}
      {(preCompetition || t.starts_at || t.location_text || t.entry_fee_cents != null || t.match_format_text) && (
        <EventInfoCard event={t} eventType={t.tournament_type} />
      )}

      {/* 4. Registration CTA / state. */}
      {t.status === 'registration_open' || t.status === 'registration_closed' ? (
        <div className="flex flex-col gap-3">
          <RegistrationCTA
            status={t.status}
            isRegistered={isRegistered}
            isFull={isFull}
            isAlternate={isAlternate}
            alternatePosition={myAlternate?.position || null}
            onOpenRegistration={() => setRegOpen(true)}
            onWithdraw={handleWithdraw}
            withdrawBusy={withdrawBusy}
            eventKind="tournament"
            eventId={t.id}
            onReload={reload}
            viewerPaid={!!user?.id && paidProfileIds.has(user.id)}
          />
        </div>
      ) : null}

      {/* 5. Registered Players (Prompt 1.1 preserved) — doubles roster + all pre-competition states. */}
      {preCompetition && t.tournament_type === 'doubles' && (
        <RegistrationRoster
          entries={t.entries}
          title="Registered Players"
          maxEntries={t.max_entries || null}
          eventType="doubles"
          paidProfileIds={paidProfileIds}
        />
      )}
      {preCompetition && t.tournament_type === 'singles' && (
        <SinglesRoster entries={t.entries} paidProfileIds={paidProfileIds} />
      )}

      {/* 5b. Alternate list (Prompt 2.2) — appears when at least one alternate exists. */}
      {preCompetition && alternates.length > 0 && (
        <AlternateList alternates={alternates} viewerProfileId={user?.id} />
      )}

      {/* 6. Event description. */}
      {t.description && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="event-description">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-2">About this event</div>
          <div className="text-[14px] text-[#2a332c] whitespace-pre-line leading-relaxed">
            {t.description}
          </div>
        </div>
      )}

      {/* 7. Event Details — currently the match format text. */}
      {t.match_format_text && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="event-details">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-2">Event Details</div>
          <div className="text-[14px] text-[#013523] whitespace-pre-line">
            {t.match_format_text}
          </div>
        </div>
      )}

      {/* 8. Payment section — only if any payment data is set. */}
      {preCompetition && (
        <PaymentSection
          event={t}
          eventKind="tournament"
          viewer={user}
          viewerRegistered={isRegistered}
          viewerPaid={viewerPaid}
          onPaid={reload}
          autoStartToken={autoStartToken}
        />
      )}

      {/* Competition phase — champion + bracket presentation preserved. */}
      {t.champion && (
        <div
          className="border border-[#c9a93a] rounded-2xl bg-gradient-to-r from-[#fffbf0] to-[#fff7e0] px-4 py-3 flex items-center gap-3"
          data-testid="tournament-header-champion"
        >
          <Trophy size={24} weight="fill" className="text-[#c9a93a] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#c9a93a] font-semibold">Champion</div>
            <div className="text-base text-[#013523] font-semibold truncate">
              <EntryName entry={t.champion} />
            </div>
            {t.runner_up && (
              <div className="text-[11px] text-[#6a766e]">
                Runner-up: <span className="text-[#2a332c]"><EntryName entry={t.runner_up} /></span>
              </div>
            )}
          </div>
        </div>
      )}

      {!preCompetition && (
        <>
          <div className="border border-[#e3dcd1] rounded-2xl p-3 bg-white" data-testid="tournament-entries-summary">
            <div className="text-[10px] uppercase tracking-wider text-[#6a766e] mb-2">
              Field · {t.entries.length} {t.entries.length === 1 ? 'entry' : 'entries'}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {t.entries.map((e) => (
                <span
                  key={e.id}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-[#f3f1ec] text-[#013523] flex items-center gap-1"
                  data-testid={`entry-chip-${e.id}`}
                >
                  {e.seed && <span className="text-[9px] text-[#6a766e]">#{e.seed}</span>}
                  <EntryName entry={e} />
                </span>
              ))}
              {t.entries.length === 0 && <div className="text-xs text-[#6a766e]">No entries yet.</div>}
            </div>
          </div>
          <BracketView tournament={t} dataTestidPrefix="public-bracket" />
        </>
      )}

      <EventRegistrationDialog
        open={regOpen}
        onOpenChange={setRegOpen}
        eventKind="tournament"
        eventId={t.id}
        eventName={t.name}
        eventType={t.tournament_type}
        composition={t.composition || 'open'}
        onRegistered={() => {
          justRegisteredRef.current = true;
          reload();
        }}
      />

      {/* Post-registration payment prompt — one-shot per successful
          registration action. Never re-opens on page revisits. */}
      <PostRegistrationPaymentPrompt
        open={payPromptOpen}
        onOpenChange={setPayPromptOpen}
        eventTypeLabel="Tournament"
        amountLabel={amountLabel}
        paymentMethod={t.payment_type}
        onPayNow={handlePayNow}
        onPayLater={handlePayLater}
      />
    </div>
  );
}

// Simple singles-only alphabetical roster (doubles uses RegistrationRoster).
function SinglesRoster({ entries = [], paidProfileIds = new Set() }) {
  const rows = entries
    .filter((e) => e.player1_profile_id)
    .map((e) => ({ id: e.id, profileId: e.player1_profile_id, name: e.player1_name || e.display_name }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="singles-roster">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Registered Players</div>
        <div className="text-[11.5px] text-[#6a766e]" data-testid="singles-roster-count">
          {rows.length} Player{rows.length === 1 ? '' : 's'} Registered
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#6a766e]" data-testid="singles-roster-empty">
          Nobody has registered yet — be the first!
        </div>
      ) : (
        <ul className="divide-y divide-[#f3f1ec]">
          {rows.map((r) => (
            <li key={r.id} className="py-2 text-[14px] text-[#013523] font-medium flex items-center gap-2" data-testid={`singles-roster-row-${r.profileId}`}>
              <MemberLink profileId={r.profileId} name={r.name} />
              {paidProfileIds.has(r.profileId) && (
                <span
                  data-testid={`singles-roster-paid-${r.profileId}`}
                  title="Paid"
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
                >
                  ✓ Paid
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
